// Pure helpers behind the consultant home's client list. Kept out of the
// component so the filtering/grouping rules are unit-testable without rendering.

/** @typedef {import('modules/Consultant/hooks/useFetchConsultantClients').ConsultantClient} ConsultantClient */

const byName = (a, b) => (a.name || '').localeCompare(b.name || '');

/**
 * Narrow clients by a case-insensitive name search and an exact country iso2.
 * Result is sorted by name.
 */
export const filterClients = (clients, { search = '', country = '' } = {}) => {
  const q = search.trim().toLowerCase();
  return clients
    .filter((c) => !q || (c.name || '').toLowerCase().includes(q))
    .filter((c) => !country || c.country?.iso2 === country)
    .sort(byName);
};

/** Distinct countries present in the client list, as `{ value: iso2, label: name }`, sorted by name. */
export const countryOptions = (clients) => {
  const byIso = new Map();
  clients.forEach((c) => {
    const iso2 = c.country?.iso2;
    if (iso2 && !byIso.has(iso2)) byIso.set(iso2, c.country.name || iso2);
  });
  return [...byIso.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/** Recently-opened ids resolved against the live list, in id order; unknown ids drop out. */
export const resolveRecents = (ids, clients) => {
  const byId = new Map(clients.map((c) => [c.id, c]));
  return ids.map((id) => byId.get(id)).filter(Boolean);
};

/**
 * Split clients into Recently opened (recency order) and the rest (by name).
 */
export const groupByRecency = (clients, recentIds) => {
  const recent = resolveRecents(recentIds, clients);
  const recentSet = new Set(recent.map((c) => c.id));
  return {
    recent,
    others: clients.filter((c) => !recentSet.has(c.id)).sort(byName),
  };
};

/**
 * One page of grouped rows. Rows are paged in display order across groups, so
 * Recently opened stays on page 1; a group spanning pages keeps its label on
 * each. `page` is clamped, so a shrinking list never strands you past the end.
 */
export const paginateGroups = (groups, page, pageSize) => {
  const total = groups.reduce((n, g) => n + g.rows.length, 0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(page, 1), totalPages);
  const from = (current - 1) * pageSize;
  const to = from + pageSize;
  let offset = 0;
  const paged = [];
  groups.forEach((g) => {
    const start = Math.max(from - offset, 0);
    const end = Math.min(to - offset, g.rows.length);
    if (start < end) paged.push({ ...g, rows: g.rows.slice(start, end) });
    offset += g.rows.length;
  });
  return {
    groups: paged,
    page: current,
    totalPages,
    total,
    start: total ? from + 1 : 0,
    end: Math.min(to, total),
  };
};

/** Installation and diagram totals across the given clients (the list footer). */
export const listTotals = (clients) =>
  clients.reduce(
    (acc, c) => ({
      installations: acc.installations + (c.installationsCount || 0),
      diagrams: acc.diagrams + (c.diagramsCount || 0),
    }),
    { installations: 0, diagrams: 0 },
  );

/** How many kinds of setup a client is missing (zero counts). Never a compliance status. */
export const missingSetup = (client) =>
  [!client.installationsCount, !client.productCount, !client.diagramsCount].filter(Boolean).length;

/** Clients missing at least one kind of setup, most missing first, then by name. */
export const needsSetup = (clients) =>
  clients
    .map((client) => ({ client, n: missingSetup(client) }))
    .filter((row) => row.n > 0)
    .sort((a, b) => b.n - a.n || byName(a.client, b.client))
    .map((row) => row.client);

/** Up to two initials for a client's avatar chip ("Acme Steel GmbH" → "AS"). */
export const clientInitials = (name) => {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return words
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
};
